import React, { forwardRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import clsx from 'clsx';
import { audioManager } from 'lib/audio/audio-manager';
import { AUDIO_TYPES } from 'lib/audio/audio.types';

import LoadingSpinner from 'assets/animation/buttons/spinner-brown.svg?react';

interface IWatchBtnProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  text?: string;
  svg?: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  isLoading?: boolean;
  className?: string;
}

export const WatchBtn = forwardRef<HTMLButtonElement, IWatchBtnProps>((props, ref) => {
  const { className, text, svg, onClick, disabled, isLoading, ...rest } = props;
  const { t } = useTranslation();

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      if (!disabled && !isLoading) {
        audioManager.playSound(AUDIO_TYPES.CLICK);
        onClick();
      }
    },
    [onClick, disabled, isLoading],
  );

  return isLoading ? (
    <button
      ref={ref}
      className={clsx(
        `min-h-[50px] cursor-not-allowed rounded-xl bg-watch-border p-0.5 outline-none sm:text-[20px]`,
        className,
      )}
      {...rest}
    >
      <div
        className={`group relative inset-0 z-10 flex h-full items-center justify-center overflow-hidden rounded-[10px] bg-watch-btn px-4 py-[11px] font-semibold shadow-watch-btn hover:bg-watch-btn-hover active:shadow-watch-btn-active sm:py-3 sm:leading-6`}
      >
        <div className="flex items-center justify-center">
          <LoadingSpinner className="h-6 w-6 animate-spin" />
        </div>
      </div>
    </button>
  ) : (
    <button
      ref={ref}
      className={clsx(
        `min-h-[50px] rounded-xl bg-watch-border p-0.5 outline-none sm:text-[20px]`,
        className,
      )}
      onClick={handleClick}
      disabled={disabled}
      {...rest}
    >
      <div
        className={`group relative inset-0 z-10 flex h-full items-center justify-center overflow-hidden rounded-[10px] bg-watch-btn px-4 py-[11px] font-semibold shadow-watch-btn hover:bg-watch-btn-hover active:shadow-watch-btn-active sm:py-3 sm:leading-6`}
      >
        <div
          className={`relative z-40 flex items-center justify-center gap-2 text-[#2E083D] transition-all duration-300 ease-out xs:tracking-normal`}
        >
          {svg}
          <span className={'whitespace-nowrap'}>{t(text ?? '')}</span>
        </div>
      </div>
    </button>
  );
});
